import { useEffect } from 'react';
import Cookies from 'universal-cookie';
import { useAuthStore } from '@src/store/modules/auth.store';
import { devError, devLog } from '@src/utils/environment';
import { SessionManager } from '@src/utils/sessionManager';

/**
 * SessionInitializer component
 *
 * This component is responsible for initializing sessions when the application loads.
 * It checks for existing sessions in sessionStorage and validates them.
 * If a valid session is found, it updates the auth store with the session data.
 */
const SessionInitializer: React.FC = () => {
  const {
    setSession,
    setOrgJWT,
    setAccountId,
    orgJWT,
    accountId
  } = useAuthStore();
  useEffect(() => {
    // This effect runs once when the component mounts
    const cookies = new Cookies();
    const initializeSession = async () => {
      try {
        // Check if we have orgJWT and accountId in cookies or auth store
        const cookieOrgJWT = cookies.get('orgJWT');
        const cookieAccountId = cookies.get('accountId');
        const currentOrgJWT = orgJWT || cookieOrgJWT;
        const currentAccountId = accountId || cookieAccountId;

        // If we don't have the necessary data, we can't initialize sessions
        // This is a normal situation when the user hasn't logged in yet
        if (!currentOrgJWT || !currentAccountId) {
          // Use devLog instead of console.log to only show in development
          devLog('No session data available yet - waiting for user to interact with a location');
          return;
        }

        // Make sure auth store is updated with the latest values
        if (cookieOrgJWT && !orgJWT) {
          setOrgJWT(cookieOrgJWT);
        }
        if (cookieAccountId && !accountId) {
          setAccountId(cookieAccountId);
        }

        // Check if we have any valid sessions in sessionStorage
        const session = SessionManager.getSession(currentAccountId);
        if (session) {
          devLog('Found valid session in sessionStorage', {
            ssId: session.ssId,
            expiredDtUtc: session.expiredDtUtc
          });

          // Update auth store with the session data
          setSession({
            schedJWT: session.schedJWT,
            ssId: session.ssId
          });

          // Update cookies for backward compatibility
          cookies.set('schedJWT', session.schedJWT, {
            path: '/',
            maxAge: 86400,
            secure: true,
            sameSite: 'strict'
          });
          cookies.set('ssId', session.ssId, {
            path: '/',
            maxAge: 86400,
            secure: true,
            sameSite: 'strict'
          });
        } else {
          devLog('No valid sessions found in sessionStorage');
        }
      } catch (error) {
        devError('Error initializing sessions', error);
      }
    };
    initializeSession();
  }, [orgJWT, accountId, setSession, setOrgJWT, setAccountId]);

  // This component doesn't render anything
  return null;
};
export default SessionInitializer;